<script setup lang="ts">
import Num from '@/components/Num.vue';
import ProductPage from '@/components/ProductPage.vue';
import ProductPill from '@/components/ProductPill.vue';
import VerticalMarquee from '@/components/VerticalMarquee.vue';
import Deck from '@/components/aero-pro-cards/Deck.vue';
import DriveTrain from '@/components/aero-pro-cards/DriveTrain.vue';
import Electronics from '@/components/aero-pro-cards/Electronics.vue';
import Portability from '@/components/aero-pro-cards/Portability.vue';
import Range from '@/components/aero-pro-cards/Range.vue';
import Safety from '@/components/aero-pro-cards/Safety.vue';
import Trucks from '@/components/aero-pro-cards/Trucks.vue';
import Usability from '@/components/aero-pro-cards/Usability.vue';
import Wheels from '@/components/aero-pro-cards/Wheels.vue';
import Why from '@/components/aero-pro-cards/Why.vue';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { type Product, getProductById } from '@/lib/shopify/product.ts';
import { useSeoMeta } from '@unhead/vue';
import { useData } from 'vike-vue/useData';
import { onMounted, ref } from 'vue';

const AERO_PRO_ID = 'gid://shopify/Product/8195298263262';

const data = useData<{ product?: Product }>();
const product = ref<Product | null>(data.product ?? null);

onMounted(async () => {
  if (!product.value) product.value = await getProductById(AERO_PRO_ID);
});

const cards = [Why, Safety, Electronics, Portability, DriveTrain, Usability, Trucks, Range, Wheels, Deck];

useSeoMeta({
  title: () => (product.value?.title ? `${product.value.title} - Parsec Skateboards` : 'Parsec Skateboards'),
});
</script>

<template>
  <ProductPage v-if="product" :product>
    <template #product-details="product">
      <div class="flex gap-2 items-center">
        <div class="bg-accent w-4 h-4 rounded-full shadow-md"></div>
        <h2>Model X In production, shipping February 2025.</h2>
      </div>

      <template v-if="(product.variant?.title ?? '').startsWith('S')">
        <ProductPill>
          <template #left><h3>Weight</h3></template>
          <template #right>
            <h3><Num value="15.5" unit="lb" /> or <Num value="7" unit="kg" /></h3>
          </template>
        </ProductPill>

        <ProductPill>
          <template #left><h3>Top Speed</h3></template>
          <template #right>
            <h3><Num value="26" unit="mph" /> or <Num value="42" unit="kmh" /></h3>
          </template>
        </ProductPill>

        <ProductPill>
          <template #left><h3>Range</h3></template>
          <template #right>
            <h3><Num min="10" max="16" unit="mi" /> or <Num min="16" max="26" unit="km" /></h3>
          </template>
        </ProductPill>
      </template>
      <template v-else-if="(product.variant?.title ?? '').startsWith('X')">
        <ProductPill>
          <template #left><h3>Weight</h3></template>
          <template #right>
            <h3><Num value="18.3" unit="lb" /> or <Num value="8.3" unit="kg" /></h3>
          </template>
        </ProductPill>

        <ProductPill>
          <template #left><h3>Top Speed</h3></template>
          <template #right>
            <h3><Num value="26" unit="mph" /> or <Num value="42" unit="kmh" /></h3>
          </template>
        </ProductPill>

        <ProductPill>
          <template #left><h3>Range</h3></template>
          <template #right>
            <h3><Num min="20" max="32" unit="mi" /> or <Num min="32" max="52" unit="km" /></h3>
          </template>
        </ProductPill>
      </template>
      <template v-else></template>

      <ProductPill>
        <template #left><h3>Sustained Power</h3></template>
        <template #right>
          <h3><Num value="2352" unit="w" /></h3>
        </template>
      </ProductPill>

      <ProductPill>
        <template #left><h3>Peak Power</h3></template>
        <template #right>
          <h3><Num value="2940" unit="w" /></h3>
        </template>
      </ProductPill>

      <ProductPill>
        <template #left><h3>Deck Dimensions</h3></template>
        <template #right>
          <h3><Num value="34.5" unit="in" /> x <Num value="10.25" unit="in" /></h3>
        </template>
      </ProductPill>
    </template>

    <MasonryWall :items="cards" class="col-span-12" :max-columns="2" :gap="16">
      <template #default="{ item }">
        <component :is="item" v-fade-in class="col-span-6 min-h-[400px] max-h-min" />
      </template>
    </MasonryWall>

    <section v-fade-in class="col-span-12 flex items-center justify-center">
      <VerticalMarquee class="thingy">
        <template #left>
          <h2>Premium</h2>
        </template>
        <template #right>
          <h2>safety.</h2>
          <h2>design.</h2>
          <h2>engineering.</h2>
          <h2>quality.</h2>
          <h2>construction.</h2>
          <h2>reliability.</h2>
          <h2>power.</h2>
        </template>
      </VerticalMarquee>
    </section>

    <section v-fade-in class="faq bg-primary rounded-lg overflow-hidden col-span-12">
      <div class="max-w-4xl w-full">
        <div class="w-full">
          <h1 v-anchor="'faq'">FAQ</h1>
        </div>

        <Accordion class="w-full" type="multiple" collapsible>
          <AccordionItem value="who">
            <AccordionTrigger>
              <h2>Who are the Aero electric skateboards for?</h2>
            </AccordionTrigger>
            <AccordionContent>
              <p>The Aeros are for:</p>

              <ul>
                <li>
                  <h3>Commuters who need a highly portable form of transportation.</h3>
                  <p>The Aero Pro is just 15.5 lb (7.0 kg) and can be carried like a traditional longboard.</p>
                </li>

                <li>
                  <h3>E-skaters who want their second board to be much lighter.</h3>
                  <p>The Aero Pro is less than half as heavy as most all-terrain boards these days.</p>
                </li>

                <li>
                  <h3>Longboarders who want a true hybrid without any motor resistance.</h3>
                  <p>The Aeros feature a drivetrain that can be disengaged in seconds without tools.</p>
                </li>

                <li>
                  <h3>Beginners who want to learn traditional longboarding.</h3>
                  <p>
                    The Aeros offer a ride experience closer to traditional longboarding than most electric skateboards.
                  </p>
                </li>

                <li>
                  <h3>People who want their boards to last a very long time.</h3>
                  <p>
                    The Aero Pro and Supreme battery packs are rated for 1000 cycles, 500 - 700 times more than the
                    typical e-board battery packs.
                  </p>
                </li>
              </ul>

              <p>
                We recommend the Aero Pro for those who prioritize portability, the Aero Supreme for those who want the
                ultimate performance-to-weight ratio, and the Aero Chill for those on a tighter budget.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="push">
            <AccordionTrigger>
              <h2>Can I push the Aero like a traditional longboard?</h2>
            </AccordionTrigger>
            <AccordionContent>
              <p>
                Yes! More so than just about any other electric skateboard. Aero’s Idler Belt Drive has far less rolling
                resistance than conventional belt drive systems. (And requires far less maintenances!)
              </p>
              <p>
                If you want no drivetrain resistance at all, disengaging the drive takes only a few seconds and requires
                no tools. Same goes for reengaging the drive.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="battery">
            <AccordionTrigger>
              <h2>Is the battery pack swappable?</h2>
            </AccordionTrigger>
            <AccordionContent>
              <p>The battery pack in the Aero Series boards is not designed for easy swapping during regular use.</p>
              <p>
                While it can be replaced if necessary, doing so requires a more involved process and should only be done
                when the battery has degraded over time or if there is a specific issue that necessitates replacement.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="waterproof">
            <AccordionTrigger>
              <h2>Is the Aero waterproof?</h2>
            </AccordionTrigger>
            <AccordionContent>
              <p>
                Water and electronics do not like each other. On the Aero, we have prepared for water ingress through
                various methods including impenetrable griptape, 704 concealed cable ports, and potted PCB.
              </p>
              <p>
                Eskates are not submarines, and neither was the Aero designed to be one, so unfortunately the Aero is
                not waterproof.
              </p>
              <p>
                Any failures caused directly or indirectly by water damage are NOT covered by our 12 months warranty, so
                please keep extra caution in the wet.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="weight">
            <AccordionTrigger>
              <h2>Am I too heavy to ride an Aero?</h2>
            </AccordionTrigger>
            <AccordionContent>
              <p>
                The Aero Pro and Aero Supreme support weights up to 300 lbs. The Aero Chill supports up to 250 lbs.
                Heavier riders may wish to increase the power output in Settings.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="wheels">
            <AccordionTrigger>
              <h2>Aren’t the stock 80mm wheels too small?</h2>
            </AccordionTrigger>
            <AccordionContent>
              <p>
                The 80mm diameter is notably smaller than the vast majority of electric skateboard wheels, but are
                considered large for longboard wheels. While there are pros and cons to this size, we chose to stay true
                to our goal of delivering a board that captures the look and ride feel of a double-drop longboard.
              </p>
              <p>
                If you wish to use larger wheels, most if not all electric skateboard wheels up to 120 mm in diameter
                are compatible. The Aeros come with “Kegel” pulleys which fit wheels from Momentum, 88 Wheel Co,
                Orangatang, and more. If you’re getting Cloudwheels, be sure to get the kit that includes their pulleys.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="kingpin">
            <AccordionTrigger>
              <h2>Why is the truck kingpin installed backwards?</h2>
            </AccordionTrigger>
            <AccordionContent>
              <p>
                The kingpin on the Aerolith (Aero’s truck) is designed to accommodate the use of two tall bushings. When
                using a standard height bushing on the roadside, extra threads on the kingpin are exposed. Pointing the
                kingpin inward protects those threads.
              </p>
              <p>
                Also, the Aerolith’s precision manufacturing has the pivot pin on the hanger snugly fitted into the
                baseplate. While this reduces slop, it also makes detaching the hanger more difficult. Pointing the
                kingpin inward allows for the board-side bushing to be changed without having to detach the hanger.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>
  </ProductPage>
</template>

<style>
.faq {
  @apply p-6 flex flex-col items-center;

  .accordion-trigger h2 {
    @apply text-left;
  }

  h1 {
    @apply text-2xl font-bold mt-4;
  }

  h2 {
    @apply text-2xl font-semibold mt-4;
  }

  h3 {
    @apply text-lg font-semibold mt-2;
  }

  p {
    @apply text-lg font-normal mt-2;
  }

  li {
    @apply pl-4;
  }

  li > p {
    @apply mt-1;
  }
}
</style>
